import { useEffect } from 'react';
import { RouterProvider } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { router } from './router';
import { useAuthStore } from '@/stores/authStore';
import { authApi } from '@/api/endpoints/auth';
import { isTokenExpired } from '@/lib/jwt';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Deduplication guard: prevents concurrent refresh calls (e.g., React StrictMode double-mount)
let refreshPromise: Promise<void> | null = null;

function AppInitializer({ children }: { children: React.ReactNode }) {
  const { setTokens, logout, initialize, getRefreshToken, getAccessToken } = useAuthStore();

  useEffect(() => {
    async function doRefresh() {
      const refreshToken = getRefreshToken();
      const accessToken = getAccessToken();

      if (!refreshToken) {
        // No refresh token — nothing to restore; clear any stale access token
        if (accessToken) logout();
        initialize();
        return;
      }

      // If the access token exists and is not expired (with 30s buffer), skip the refresh
      if (accessToken && !isTokenExpired(accessToken, 30)) {
        initialize();
        return;
      }

      try {
        const { data } = await authApi.refreshToken({
          accessToken: accessToken ?? '',
          refreshToken,
        });
        if (data.accessToken && data.refreshToken) {
          setTokens(data.accessToken, data.refreshToken);
        }
      } catch {
        // Refresh failed — clear persisted tokens so the user logs in again
        logout();
      } finally {
        initialize();
      }
    }

    function tryRefresh() {
      if (!refreshPromise) {
        refreshPromise = doRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      return refreshPromise;
    }

    tryRefresh();
  }, [setTokens, logout, initialize, getRefreshToken, getAccessToken]);

  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppInitializer>
        <RouterProvider router={router} />
      </AppInitializer>
    </QueryClientProvider>
  );
}
